<template>
  <div class="group relative cursor-pointer overflow-hidden text-left">
    <div class="flex items-stretch">
      <div class="flex w-12 shrink-0 flex-col">
        <span
          class="bg-static-success-mid rounded-tl-lg -mb-1 block w-full grow"
        />
        <svg
          class="w-full text-[#96F5BD]"
          fill="none"
          viewBox="0 0 13 26"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13 26H0.00683594C0.0153194 25.4113 0.0383112 25.0428 0.113281 24.7773C0.332557 24.0012 0.398933 23.8952 1 23.3574C1.30979 23.0803 2.31611 22.5882 4.3291 21.6045C7.54185 20.0344 9.75 16.771 9.75 13C9.75 9.22899 7.54185 5.96557 4.3291 4.39551C2.31611 3.41177 1.30979 2.91973 1 2.64258C0.398933 2.10478 0.332557 1.99882 0.113281 1.22266C0.0383112 0.957222 0.0153194 0.588658 0.00683594 0H13V26Z"
            fill="currentColor"
          />
        </svg>
        <span
          class="bg-static-success-mid rounded-bl-lg -mt-1 block w-full grow"
        />
      </div>
      <div class="bg-static-success-mid flex items-center">
        <component :is="icon" v-if="icon" class="body-2-bold mx-4 w-24" />
      </div>
      <div class="flex w-6 shrink-0 flex-col justify-between">
        <svg
          class="w-full text-[#96F5BD]"
          fill="currentColor"
          viewBox="0 0 10 5"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 0C0 2.76142 2.23858 5 5 5H0V0Z" />
          <path d="M10 5H5C7.76142 5 10 2.76142 10 0V5Z" />
        </svg>

        <template v-for="i in 4" :key="`dot-${i}`">
          <svg
            v-if="i > 1"
            class="w-full text-[#96F5BD]"
            fill="currentColor"
            viewBox="0 0 10 10"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0V10H5C7.76142 10 10 7.76142 10 5C10 2.23858 7.76142 0 5 0C2.23858 0 0 2.23858 0 5C0 7.76142 2.23858 10 5 10H0V0H10Z"
            />
          </svg>
          <span class="bg-static-success-mid block w-full grow" />
        </template>
        <svg
          class="w-full text-[#96F5BD]"
          fill="currentColor"
          viewBox="0 0 10 5"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 5C0 2.23858 2.23858 0 5 0H0V5Z" />
          <path d="M10 0H5C7.76142 0 10 2.23858 10 5V0Z" />
        </svg>
      </div>

      <div class="bg-static-success-mid flex grow items-center">
        <slot />
      </div>
      <div class="flex w-12 shrink-0 flex-col">
        <span
          class="bg-static-success-mid rounded-tr-lg -mb-1 block w-full grow"
        />
        <svg
          class="w-full text-[#96F5BD]"
          fill="none"
          viewBox="0 0 13 26"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.13631e-06 -1.1359e-06L12.9932 0C12.9847 0.5887 12.9617 0.9572 12.8867 1.2227C12.6674 1.9988 12.6011 2.1048 12 2.6426C11.6902 2.9197 10.6839 3.4118 8.6709 4.3955C5.45815 5.9656 3.25 9.229 3.25 13C3.25 16.771 5.45815 20.0344 8.6709 21.6045C10.6839 22.5882 11.6902 23.0803 12 23.3574C12.6011 23.8952 12.6674 24.0012 12.8867 24.7773C12.9617 25.0428 12.9847 25.4113 12.9932 26L-1.13668e-06 26L1.13631e-06 -1.1359e-06Z"
            fill="currentColor"
          />
        </svg>
        <span
          class="bg-static-success-mid rounded-br-lg -mt-1 block w-full grow"
        />
      </div>
    </div>
    <div
      class="absolute -left-[200px] top-0 z-10 h-200 w-200 md:group-hover:left-full md:group-hover:transition-[left] md:group-hover:duration-700"
      :class="[$style.slide, { [$style.slideActive]: isVisible }]"
    ></div>
  </div>
</template>

<script lang="ts" setup>
import { type Component, onMounted, ref } from 'vue'

const isVisible = ref(false)

onMounted(() => {
  isVisible.value = true
})

defineProps<{
  icon: Component
}>()
</script>

<style module>
@keyframes slideOnce {
  0% {
    left: -200px;
  }
  100% {
    left: 100%;
  }
}

@keyframes slideInfinite {
  0% {
    left: -200px;
  }
  10%,
  100% {
    left: 100%;
  }
}

.slide {
  background: linear-gradient(
    105deg,
    transparent 15%,
    rgba(255, 255, 255, 0.8) 50%,
    transparent 85%
  );
}

.slideActive {
  animation: slideOnce 700ms;
  animation-delay: 200ms;
  @media (max-width: theme('screens.md')) {
    animation: slideInfinite 7s;
    animation-iteration-count: infinite;
  }
}
</style>
